import { useState, useRef, ChangeEvent } from "react";
import { Form, Formik, Field } from "formik";
import * as Yup from "yup";
import { FiCamera, FiLock, FiUser } from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../services/api";
import { PageTitle } from "../../components/PageTitle";
import { Loading } from "../../components/Loading";

interface PasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const passwordSchema = Yup.object().shape({
  currentPassword: Yup.string().required("Senha atual é obrigatória"),
  newPassword: Yup.string()
    .min(6, "A nova senha deve ter no mínimo 6 caracteres")
    .required("Nova senha é obrigatória"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "As senhas não coincidem")
    .required("Confirmação de senha é obrigatória"),
});

export function Profile() {
  const { user, updateUser } = useAuth();
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  const handleAvatarChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Validar tipo de arquivo
      if (!file.type.startsWith("image/")) {
        // eslint-disable-next-line no-alert
        alert("Por favor, selecione uma imagem válida.");
        return;
      }

      // Validar tamanho (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        // eslint-disable-next-line no-alert
        alert("A imagem deve ter no máximo 5MB.");
        return;
      }

      setLoading(true);

      try {
        const formData = new FormData();
        formData.append("avatar", file);

        const response = await api.patch("/users/avatar", formData);

        updateUser({
          ...user,
          avatar_url: response.data.avatar_url,
        });

        // eslint-disable-next-line no-alert
        alert("Avatar atualizado com sucesso!");
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erro ao atualizar avatar:", error);
        // eslint-disable-next-line no-alert
        alert("Erro ao atualizar avatar. Tente novamente.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUpdatePassword = async (
    values: PasswordFormValues,
    { resetForm }: { resetForm: () => void },
  ) => {
    setPasswordError("");
    setPasswordSuccess("");
    setLoading(true);

    try {
      await api.patch("/users/password", {
        current_password: values.currentPassword,
        new_password: values.newPassword,
      });

      setPasswordSuccess("Senha atualizada com sucesso!");
      resetForm();
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error("Erro ao atualizar senha:", error);
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response &&
        error.response.data &&
        typeof error.response.data === "object" &&
        "message" in error.response.data
      ) {
        setPasswordError(error.response.data.message as string);
      } else {
        setPasswordError("Erro ao atualizar senha. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loading />}
      <div className="container mx-auto px-4">
        <PageTitle
          title="Meu Perfil"
          backButton
          backPath="/tech-visit-reports"
        />

        <div className="max-w-2xl mx-auto">
          {/* Seção do Avatar */}
          <div className="bg-white rounded-lg p-6 shadow-md mb-6">
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
              <div className="relative flex-shrink-0">
                <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                  {user?.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={user.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <FiUser size={40} className="text-gray-400" />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => avatarInputRef.current?.click()}
                  className="absolute bottom-0 right-0 w-8 h-8 bg-[#0085BD] rounded-full flex items-center justify-center text-white hover:bg-[#006a96] transition-colors"
                  title="Alterar foto"
                >
                  <FiCamera size={16} />
                </button>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
              </div>

              <div className="text-center sm:text-left min-w-0 flex-1">
                <h2 className="text-xl font-semibold text-gray-800 break-words">
                  {user?.name}
                </h2>
                <p className="text-gray-500 break-all text-sm sm:text-base">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Seção de Alteração de Senha */}
          <div className="bg-white rounded-lg p-6 shadow-md">
            <div className="flex items-center gap-2 mb-6">
              <FiLock size={20} className="text-[#0085BD]" />
              <h3 className="text-lg font-semibold text-gray-800">
                Alterar Senha
              </h3>
            </div>

            <Formik
              initialValues={{
                currentPassword: "",
                newPassword: "",
                confirmPassword: "",
              }}
              validationSchema={passwordSchema}
              onSubmit={handleUpdatePassword}
            >
              {({ errors, touched }) => (
                <Form>
                  <div className="space-y-4">
                    <div>
                      <div
                        className={`w-full min-h-[52px] flex items-center bg-white rounded-lg border ${
                          errors.currentPassword && touched.currentPassword
                            ? "border-2 border-red-500"
                            : "border-gray-300 focus-within:border-[#0085BD] focus-within:ring-1 focus-within:ring-[#0085BD]"
                        }`}
                      >
                        <Field
                          name="currentPassword"
                          type="password"
                          placeholder="Senha Atual"
                          className="px-4 w-full h-12 text-sm rounded-lg placeholder-gray-400 text-gray-700 focus:outline-none focus:ring-0 border-none"
                        />
                      </div>
                      {errors.currentPassword && touched.currentPassword && (
                        <div className="mt-1 text-xs text-red-500">
                          {errors.currentPassword}
                        </div>
                      )}
                    </div>

                    <div>
                      <div
                        className={`w-full min-h-[52px] flex items-center bg-white rounded-lg border ${
                          errors.newPassword && touched.newPassword
                            ? "border-2 border-red-500"
                            : "border-gray-300 focus-within:border-[#0085BD] focus-within:ring-1 focus-within:ring-[#0085BD]"
                        }`}
                      >
                        <Field
                          name="newPassword"
                          type="password"
                          placeholder="Nova Senha"
                          className="px-4 w-full h-12 text-sm rounded-lg placeholder-gray-400 text-gray-700 focus:outline-none focus:ring-0 border-none"
                        />
                      </div>
                      {errors.newPassword && touched.newPassword && (
                        <div className="mt-1 text-xs text-red-500">
                          {errors.newPassword}
                        </div>
                      )}
                    </div>

                    <div>
                      <div
                        className={`w-full min-h-[52px] flex items-center bg-white rounded-lg border ${
                          errors.confirmPassword && touched.confirmPassword
                            ? "border-2 border-red-500"
                            : "border-gray-300 focus-within:border-[#0085BD] focus-within:ring-1 focus-within:ring-[#0085BD]"
                        }`}
                      >
                        <Field
                          name="confirmPassword"
                          type="password"
                          placeholder="Confirmar Nova Senha"
                          className="px-4 w-full h-12 text-sm rounded-lg placeholder-gray-400 text-gray-700 focus:outline-none focus:ring-0 border-none"
                        />
                      </div>
                      {errors.confirmPassword && touched.confirmPassword && (
                        <div className="mt-1 text-xs text-red-500">
                          {errors.confirmPassword}
                        </div>
                      )}
                    </div>

                    {passwordError && (
                      <div className="text-red-500 text-sm">
                        {passwordError}
                      </div>
                    )}

                    {passwordSuccess && (
                      <div className="text-green-500 text-sm">
                        {passwordSuccess}
                      </div>
                    )}

                    <button
                      type="submit"
                      className="w-full h-12 bg-[#0085BD] text-white rounded-lg font-medium hover:bg-[#006a96] transition-colors"
                    >
                      Atualizar Senha
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </>
  );
}
